/**
 * Bandeja de emojis.
 *
 * Serve tanto para escrever quanto para reagir, porque a escolha é a mesma nos
 * dois casos e manter duas listas separadas só criaria a chance de uma ficar
 * para trás da outra.
 *
 * Os usados recentemente aparecem primeiro e ficam salvos: na prática quase todo
 * mundo repete um punhado pequeno de emojis, e obrigar a procurar de novo o
 * mesmo de sempre é o que torna uma bandeja irritante.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { buscarEmojis, CATEGORIAS_DE_EMOJI, TODOS_OS_EMOJIS } from '@/lib/emojis'
import { usePreferencesStore } from '@/store/preferences'

export function EmojiPicker({
  onEscolher,
  onFechar,
  /** Alinhamento do painel em relação ao botão que o abriu. */
  alinhamento = 'direita',
}: {
  onEscolher: (emoji: string) => void
  onFechar: () => void
  alinhamento?: 'direita' | 'esquerda'
}) {
  const [termo, setTermo] = useState('')
  const [categoriaAtiva, setCategoriaAtiva] = useState(CATEGORIAS_DE_EMOJI[0]!.id)
  const buscaRef = useRef<HTMLInputElement>(null)
  const painelRef = useRef<HTMLDivElement>(null)
  const listaRef = useRef<HTMLDivElement>(null)

  const recentes = usePreferencesStore((s) => s.emojisRecentes)
  const registrarEmoji = usePreferencesStore((s) => s.registrarEmoji)

  /*
    A bandeja abre para cima por padrão, que é o certo para o campo de escrever,
    no rodapé. Mas ela também é aberta a partir da barra de ações de uma
    mensagem, e nas primeiras mensagens do canal não há espaço acima: o painel
    ficaria cortado na borda de cima da janela. A medida decide o lado depois de
    montar, antes da pintura, então não aparece salto.
  */
  const [paraBaixo, setParaBaixo] = useState(false)

  useLayoutEffect(() => {
    const caixa = painelRef.current?.getBoundingClientRect()
    if (caixa && caixa.top < 8) setParaBaixo(true)
  }, [])

  // O foco vai direto para a busca: quem abre a bandeja com um emoji em mente
  // digita, e quem não tem só ignora e rola a lista.
  useEffect(() => {
    buscaRef.current?.focus()
  }, [])

  useEffect(() => {
    const aoPressionar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        onFechar()
      }
    }
    const aoClicarFora = (evento: MouseEvent) => {
      if (!painelRef.current?.contains(evento.target as Node)) onFechar()
    }

    document.addEventListener('keydown', aoPressionar, true)
    document.addEventListener('mousedown', aoClicarFora)
    return () => {
      document.removeEventListener('keydown', aoPressionar, true)
      document.removeEventListener('mousedown', aoClicarFora)
    }
  }, [onFechar])

  const resultados = useMemo(() => (termo ? buscarEmojis(termo) : null), [termo])

  const escolher = (emoji: string) => {
    registrarEmoji(emoji)
    onEscolher(emoji)
  }

  /** Leva a lista até a categoria clicada na barra de atalhos. */
  const irPara = (id: string) => {
    setCategoriaAtiva(id)
    setTermo('')
    const alvo = listaRef.current?.querySelector<HTMLElement>(`[data-categoria="${id}"]`)
    alvo?.scrollIntoView({ block: 'start' })
  }

  const recentesValidos = recentes.filter((char) => TODOS_OS_EMOJIS.some((e) => e.char === char))

  return (
    <div
      ref={painelRef}
      role="dialog"
      aria-label="Escolher emoji"
      className={cn(
        // A largura cede antes da borda da tela: num celular estreito a bandeja
        // inteira não cabe, e sair pela lateral é pior do que ficar menor.
        'absolute z-50 w-80 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg bg-surface-overlay shadow-2xl animate-fade-in',
        paraBaixo ? 'top-9' : 'bottom-11',
        alinhamento === 'direita' ? 'right-0' : 'left-0',
      )}
    >
      <div className="flex items-center gap-2 px-2.5 pt-2.5 pb-2">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-content-muted" />
          <input
            ref={buscaRef}
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Buscar emoji"
            className="h-8 w-full rounded-md bg-surface-deepest pr-2 pl-7 text-[13px] text-content-primary outline-none placeholder:text-content-muted"
          />
        </div>
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar"
          className="rounded p-1 text-content-muted transition-colors hover:text-content-primary"
        >
          <X className="size-4" />
        </button>
      </div>

      {!termo ? (
        <div className="flex gap-0.5 px-2 pb-1.5">
          {CATEGORIAS_DE_EMOJI.map((categoria) => (
            <button
              key={categoria.id}
              type="button"
              onClick={() => irPara(categoria.id)}
              title={categoria.nome}
              aria-label={categoria.nome}
              className={cn(
                'flex size-7 items-center justify-center rounded text-base transition-colors',
                categoriaAtiva === categoria.id
                  ? 'bg-surface-active'
                  : 'hover:bg-surface-hover',
              )}
            >
              {categoria.icone}
            </button>
          ))}
        </div>
      ) : null}

      <div ref={listaRef} className="max-h-72 overflow-y-auto px-2 pb-2">
        {resultados ? (
          resultados.length === 0 ? (
            <p className="px-2 py-10 text-center text-[13px] text-content-muted">
              Nenhum emoji para “{termo}”.
            </p>
          ) : (
            <Grade emojis={resultados.map((e) => e.char)} aoEscolher={escolher} />
          )
        ) : (
          <>
            {recentesValidos.length > 0 ? (
              <section>
                <Titulo>Usados recentemente</Titulo>
                <Grade emojis={recentesValidos} aoEscolher={escolher} />
              </section>
            ) : null}

            {CATEGORIAS_DE_EMOJI.map((categoria) => (
              <section key={categoria.id} data-categoria={categoria.id}>
                <Titulo>{categoria.nome}</Titulo>
                <Grade emojis={categoria.emojis.map((e) => e.char)} aoEscolher={escolher} />
              </section>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="sticky top-0 bg-surface-overlay px-1 py-1.5 text-[11px] font-semibold tracking-wide text-content-muted uppercase">
      {children}
    </h3>
  )
}

function Grade({
  emojis,
  aoEscolher,
}: {
  emojis: string[]
  aoEscolher: (emoji: string) => void
}) {
  return (
    <div className="grid grid-cols-8 gap-0.5">
      {emojis.map((emoji, indice) => (
        <button
          // O mesmo emoji pode aparecer duas vezes entre recentes e categoria.
          key={`${emoji}-${indice}`}
          type="button"
          onClick={() => aoEscolher(emoji)}
          className="flex size-8 items-center justify-center rounded text-xl transition-colors hover:bg-surface-hover"
        >
          {emoji}
        </button>
      ))}
    </div>
  )
}
